// The intro / game-over / level-clear veils, HUD, legend, city chip list,
// onboarding hints, the sound toggle and the pause button — everything that reads
// session/world state and writes it to the DOM. Functions take `(dom,
// session, ...)` rather than closing over module state.
import type { World } from '../sim/index.ts';
import { LEVELS, aircraftType, createWorld, flyOn, landingPoints, levelById } from '../sim/index.ts';
import type { Renderer } from '../render/canvas.ts';
import { nextLevel, runScoreLine } from './game.ts';
import { isUnlocked, recordScore, saveProgress } from './progress.ts';
import { Onboarding } from './onboarding.ts';
import type { Dom, Session } from './session.ts';
import { applyRendererSize, computeViewport } from './viewport.ts';
import { hideInstallHint, releaseWakeLock, requestWakeLock } from './platform.ts';

// ---------- HUD ----------
export function updateHud(dom: Dom, session: Session, w: World): void {
  session.hudPoints = w.points;
  dom.cityEl.textContent = w.level.city;
  // Working towards the baseline, the counter is a target to reach. Past it —
  // the city is unlocked and the player chose to stay — a denominator would
  // only say "done" over and over, so the score stands on its own.
  dom.scoreEl.textContent = w.endless ? `${w.points}` : `${w.points} / ${w.level.baseline}`;
}

/** The legend doubles as the tariff: what each type in this city's mix is
 *  worth here. It is the only place the player can read that a jet pays four
 *  times a light, and that both pay more the further down the campaign. */
export function buildLegend(dom: Dom, w: World): void {
  dom.legendEl.innerHTML = w.level.mix.map(([id]) => {
    const t = aircraftType(id);
    return `<span><i style="background:${t.color}"></i>${t.label} ${landingPoints(t, w.level)}</span>`;
  }).join('');
}

// ---------- sound toggle (Q6) ----------
// Lives outside `#hud` (always in the DOM, high z-index) so the same button
// works on the intro veil and in play without duplicating markup.
export function refreshSoundToggle(dom: Dom, session: Session): void {
  dom.soundToggle.textContent = session.sound.isMuted ? 'muted' : 'sound';
  dom.soundToggle.classList.toggle('muted', session.sound.isMuted);
}

// ---------- pause ----------
// Shares the top-right corner with the sound toggle, but is only offered
// while a run is live — there is nothing to pause on a veil. While paused the
// same button reads "resume", so the corner is one toggle whichever way the
// player got there (button, Esc, or a tap on the veil).
export function refreshPauseToggle(dom: Dom, session: Session): void {
  const live = session.appState === 'playing' || session.appState === 'paused';
  dom.pauseToggle.classList.toggle('hidden', !live);
  dom.pauseToggle.textContent = session.appState === 'paused' ? 'resume' : 'pause';
}

/** Freeze a live run. The loop only steps the sim in 'playing' (loop.ts), so
 *  flipping the app state is the whole pause: the world is untouched and
 *  resuming is exact — no catch-up, no lost time. The bed fades out
 *  with the veil, the way it does on every other veil. */
export function pauseGame(dom: Dom, session: Session): void {
  if (session.appState !== 'playing') return;
  session.appState = 'paused';
  session.acc = 0;
  refreshPauseToggle(dom, session);
  dom.pauseVeil.classList.remove('hidden');
  releaseWakeLock(session);
  session.sound.bedOut();
}

export function resumeGame(dom: Dom, session: Session): void {
  if (session.appState !== 'paused') return;
  session.appState = 'playing';
  session.last = performance.now(); // resume without a time jump
  refreshPauseToggle(dom, session);
  dom.pauseVeil.classList.add('hidden');
  session.sound.bedIn();
  void requestWakeLock(session);
}

/** The pause button and Esc both mean "the other one of the two". */
export function togglePause(dom: Dom, session: Session): void {
  if (session.appState === 'playing') pauseGame(dom, session);
  else if (session.appState === 'paused') resumeGame(dom, session);
}

/** Leave a paused run for the city list. The points scored so far still count
 *  towards the city's best — they were earned, only the run stops here — and
 *  the list underneath the intro shows them straight away. Unlocking is not
 *  in question: reaching the baseline already ended the level as 'cleared'. */
export function quitToMenu(renderer: Renderer, dom: Dom, session: Session): void {
  const world = session.world;
  if (session.appState !== 'paused' || !world) return;
  session.progress = recordScore(session.progress, world.level.id, world.points);
  saveProgress(session.progress);
  showIntro(renderer, dom, session);
}

// ---------- onboarding hints (Q3: draw-to-runway, tap-to-hold) ----------
export function setHintVisible(el: HTMLDivElement, visible: boolean): void {
  el.classList.toggle('visible', visible);
  el.setAttribute('aria-hidden', visible ? 'false' : 'true');
}

export function hideHints(dom: Dom, session: Session): void {
  setHintVisible(dom.hint1El, false);
  setHintVisible(dom.hint2El, false);
  session.hint1Shown = false;
  session.hint2Shown = false;
}

/** Called once per fixed sim step (simulated time) and once after a committed
 *  path/hold input event — cheap state read, DOM is only touched on change. */
export function syncHints(dom: Dom, session: Session): void {
  const h1 = session.onboarding?.hint1Visible ?? false;
  const h2 = session.onboarding?.hint2Visible ?? false;
  if (h1 !== session.hint1Shown) { setHintVisible(dom.hint1El, h1); session.hint1Shown = h1; }
  if (h2 !== session.hint2Shown) { setHintVisible(dom.hint2El, h2); session.hint2Shown = h2; }
}

// ---------- city list ----------
/** The last unlocked level in campaign order — the one the player would
 *  naturally play next. Used both to accent that row in the list and to
 *  choose which level's map the intro veil previews. */
export function nextToPlayLevelId(session: Session): string {
  let id = LEVELS[0].id;
  for (const level of LEVELS) {
    if (isUnlocked(session.progress, level.id, session.dev)) id = level.id;
  }
  return id;
}

/** Rebuilds the intro veil's level list from LEVELS + progress — the single
 *  place the list's rows and numbers come from (Q5). Called on boot and
 *  every time progress changes (after a clear) or the intro is shown again,
 *  so a locked level, a fresh best, or a later campaign entry always reads
 *  correctly without a second copy of this state living in the DOM. */
export function refreshCityChips(dom: Dom, session: Session): void {
  const nextId = nextToPlayLevelId(session);
  dom.citiesEl.innerHTML = LEVELS.map(level => {
    const unlocked = isUnlocked(session.progress, level.id, session.dev);
    const nameClass = level.id === nextId ? ' class="lvl-name next"' : ' class="lvl-name"';
    const stat = unlocked ? `${session.progress.best[level.id] ?? 0} / ${level.baseline}` : 'locked';
    return `<div class="row${unlocked ? '' : ' locked'}" data-lvl="${level.id}">` +
      `<span${nameClass}>${level.city}</span><span class="stat">${stat}</span></div>`;
  }).join('');
}

// ---------- level flow ----------
function randomSeed(): number {
  return (Math.random() * 0xffffffff) >>> 0;
}

function createPreviewWorld(renderer: Renderer, session: Session, levelId: string): World {
  const level = levelById(levelId);
  const v = computeViewport();
  applyRendererSize(renderer, session, v);
  renderer.reset();
  const w = createWorld(level, v.logicalW, v.logicalH, 0);
  // the intro veil shows the map, not a plane in flight — mirrors the
  // prototype, which never spawns anything before "tap to begin"
  w.planes = [];
  w.schedule = [];
  return w;
}

export function startLevel(renderer: Renderer, dom: Dom, session: Session, levelId: string, seed?: number): void {
  const level = levelById(levelId);
  const v = computeViewport();
  applyRendererSize(renderer, session, v);
  renderer.reset();
  const world = createWorld(level, v.logicalW, v.logicalH, seed ?? randomSeed(), { record: session.record });
  session.world = world;
  session.lastWorldState = world.state;
  session.appState = 'playing';
  session.dragPlaneId = null;
  session.acc = 0;
  session.last = performance.now();
  session.onboarding = new Onboarding(levelId);
  hideHints(dom, session);
  updateHud(dom, session, world);
  buildLegend(dom, world);
  dom.hud.style.display = 'block';
  dom.legendEl.style.display = 'flex';
  dom.intro.classList.add('hidden');
  dom.over.classList.add('hidden');
  dom.clearVeil.classList.add('hidden');
  dom.pauseVeil.classList.add('hidden');
  refreshPauseToggle(dom, session);
  hideInstallHint(dom);
  session.sound.setLevel(level);
  session.sound.bedIn();
  void requestWakeLock(session);
}

/** Back to the intro veil's level list (Q5's "or choose a city" on the clear
 *  veil) — a calm map preview of the next level to play, same as boot()'s
 *  first paint, with fresh numbers in the list underneath. Not used for the
 *  "tap to try again" path: a loss still retries the same level directly. */
export function showIntro(renderer: Renderer, dom: Dom, session: Session): void {
  const world = createPreviewWorld(renderer, session, nextToPlayLevelId(session));
  session.world = world;
  session.appState = 'intro';
  session.lastWorldState = world.state;
  session.onboarding = null;
  hideHints(dom, session);
  dom.hud.style.display = 'none';
  dom.legendEl.style.display = 'none';
  releaseWakeLock(session);
  refreshCityChips(dom, session);
  dom.over.classList.add('hidden');
  dom.clearVeil.classList.add('hidden');
  dom.pauseVeil.classList.add('hidden');
  refreshPauseToggle(dom, session);
  dom.intro.classList.remove('hidden');
  session.sound.bedOut();
}

// Q4 failure feel: the sim already stopped (world.state === 'dead') and the
// renderer is animating a soft coral bloom at the collision point off the
// wall clock (src/render/canvas.ts). Keep the `over` veil hidden — and so,
// via its own CSS, uninteractive (`.veil.hidden{pointer-events:none}`) and
// the pointer input layer already ignores taps once `world.state !== 'playing'`
// — for the same quiet stretch, then reveal just "tap to try again".
const GAME_OVER_QUIET_MS = 1500;
export function showGameOver(dom: Dom): void {
  setTimeout(() => dom.over.classList.remove('hidden'), GAME_OVER_QUIET_MS);
}

/** What the run was worth, and what this city had seen before it — written
 *  while the veil is still hidden, so it is already there when the quiet
 *  stretch ends. `prevBest` is the standing best *before* this run, so a run
 *  that merely equals it is not announced as beating it. */
export function showRunScore(dom: Dom, w: World, prevBest: number): void {
  const line = runScoreLine(w.points, prevBest);
  dom.overScore.innerHTML = `${w.points}${line ? `<span class="best">${line}</span>` : ''}`;
}

export function showLevelClear(dom: Dom, w: World): void {
  const nxt = nextLevel(LEVELS, w.level.id);
  const run = `${w.points} points from ${w.arrivals} arrivals, without a scratch`;
  if (nxt) {
    dom.clearTitle.textContent = 'skies cleared';
    const extra = nxt.id === 'lisbon' ? ' — coastal winds, and the helicopters land only on the pad' : '';
    dom.clearText.textContent = `${w.level.city}: ${run}. Next stop: ${nxt.city}${extra}.`;
  } else {
    dom.clearTitle.textContent = 'all skies quiet';
    dom.clearText.textContent = `${w.level.city}: ${run}. Every city is yours, controller.`;
  }
  setTimeout(() => dom.clearVeil.classList.remove('hidden'), 600);
}

/** Stay on a city that is already unlocked and fly it for score alone. The
 *  world is mid-air and untouched — nothing restarts, the veil simply lifts. */
export function keepFlying(dom: Dom, session: Session): void {
  const world = session.world;
  if (!world || world.state !== 'cleared') return;
  flyOn(world);
  session.lastWorldState = world.state;
  session.appState = 'playing';
  dom.clearVeil.classList.add('hidden');
  refreshPauseToggle(dom, session);
  updateHud(dom, session, world);
  session.sound.bedIn();
  void requestWakeLock(session);
}
